import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { get } from 'lodash';

import CoreLink from '../../components/core_link';
import { buildCSPClick } from '../../elog/mparticle_tracker';

import { Commons_Cms_CspFeatureQuery } from '../../gql/graphql';
import SanitizedRender from '../../components/sanitized_render';
import { imageUploadURL, prefilledListingFromCSP } from '../../url_helpers';

import {
  findLowestPrice,
  BrandMetaData,
  NameMetaData,
  PriceMetaData,
} from '../lib/metadata_helper';
import { IUserContext, withUserContext } from '../../components/user_context_provider';
import { RCIcon, RCImage } from '@reverbdotcom/cadence/components';
import { ChevronRightIcon } from '@reverbdotcom/cadence/icons/react';

const COMPONENT_NAME = 'CspVerticalFeature';

type Commons_Cms_CspFeatureQuery_Csps = Commons_Cms_CspFeatureQuery['csps'][0];

export interface IProps extends IUserContext {
  csp?: Commons_Cms_CspFeatureQuery_Csps;
  description?: string;
  hidePrices?: boolean;
  splitPrices?: boolean;
  imageOverride?: string;
  wide?: boolean;
  useCspSummary?: boolean;
  position?: number;
  shouldLinkToSellForm?: boolean;
  trackingName?: string;
}

export class CspVerticalFeature extends React.Component<IProps, null> {
  getLowestPrice() {
    return findLowestPrice(this.props.csp)?.display;
  }

  getUsedPrice() {
    return get(this.props.csp, 'inventory.usedLowPrice.display', null);
  }

  getNewPrice() {
    return get(this.props.csp, 'inventory.newLowPrice.display', null);
  }

  trackingName() {
    return this.props.trackingName ?? COMPONENT_NAME;
  }

  destinationPath() {
    if (!this.props.csp) { return null; }

    if (this.props.shouldLinkToSellForm) {
      return prefilledListingFromCSP(this.props.csp.id);
    }

    return this.props.csp.webLink.href;
  }

  buttonText() {
    if (this.props.shouldLinkToSellForm) {
      return I18n.t('cms.cspFeature.sellYours');
    }

    return I18n.t('cms.cspFeature.shopNow');
  }

  priceTranslationKey(format) {
    if (this.props.shouldLinkToSellForm) {
      return `cms.cspFeature.pricing.selling.${format}`;
    }

    return `cms.cspFeature.pricing.buying.${format}`;
  }

  renderPlaceholder() {
    const classes = classNames(
      'csp-vertical-feature',
      'csp-vertical-feature--placeholder',
    );

    return (
      <article
        className={classes}
      >
        <div
          className="csp-vertical-feature__image"
        >
          <div
            className="csp-vertical-feature__image__inner"
          />
        </div>
        <div className="csp-vertical-feature__text" />
      </article>
    );

  }

  renderPrice() {
    if (this.props.hidePrices) { return null; }
    if (!this.getNewPrice() && !this.getUsedPrice()) { return null; }

    if (!this.props.splitPrices) {
      return (
        <div className="csp-vertical-feature__price">
          <div className="csp-vertical-feature__price__inner">
            {I18n.t(
              this.priceTranslationKey('priceFrom'),
              { price: this.getLowestPrice() },
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="csp-vertical-feature__price">
        <div className="csp-vertical-feature__price__inner">
          {this.getNewPrice() &&
            <div className="csp-vertical-feature__split-price">
              {I18n.t(this.priceTranslationKey('newPriceFrom'), { price: this.getNewPrice() })}
            </div>
          }
          {this.getUsedPrice() &&
            <div className="csp-vertical-feature__split-price">
              {I18n.t(this.priceTranslationKey('usedPriceFrom'), { price: this.getUsedPrice() })}
            </div>
          }
        </div>
      </div>
    );
  }

  renderCSPSummary(description) {
    const summaryFallback = this.props.useCspSummary && this.props.csp.summary;
    const summary = description || summaryFallback;
    if (!summary) return;

    return (
      <SanitizedRender
        htmlTag="div"
        className="csp-vertical-feature__description"
        html={summary}
      />
    );
  }

  clickEvent() {
    return {
      ...buildCSPClick(this.props.csp),
      displayStyle: this.trackingName(),
      position: this.props.position,
    };
  }

  render() {
    const { csp, description } = this.props;
    const classes = classNames(
      'csp-vertical-feature',
      { 'csp-vertical-feature--wide': this.props.wide },
    );

    if (!csp) return this.renderPlaceholder();

    return (
      <article
        className={classes}
      >
        <div
          className="csp-vertical-feature__image"
        >
          <RCImage
            src={this.props.imageOverride ? imageUploadURL(this.props.imageOverride) : this.props.csp.image.source}
            imageBox="auto"
            overlay="offwhite"
            borderRadius="lg"
          />
        </div>
        <div className="csp-vertical-feature__text">
          <h3 className="csp-vertical-feature__title">
            <CoreLink
              to={this.destinationPath()}
              clickEvent={this.clickEvent()}
            >
              {csp.title}
            </CoreLink>
          </h3>
          {this.renderCSPSummary(description)}
          <div className="csp-vertical-feature__footer">
            {this.renderPrice()}
            <BrandMetaData csp={this.props.csp} />
            <NameMetaData csp={this.props.csp} />
            <PriceMetaData
              csp={this.props.csp}
            />
            <span
              className="csp-vertical-feature__shop-link"
            >
              {this.buttonText()}
              <RCIcon icon={<ChevronRightIcon />} size={5} />
            </span>
          </div>
        </div>
      </article>
    );
  }
}

export default withUserContext(CspVerticalFeature);
